import _, {
  map, pickBy, omit, debounce, startCase,
} from 'lodash';
import React, {
  useState, useCallback, useEffect,
} from 'react';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import qs from 'qs';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload';
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import { resolveTo, permitCheck } from '~/commons/helpers';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import useComparable from '~/commons/hooks/useComparable';
import useSearchParams from '~/commons/hooks/useSearchParams';
import ListHttpTable from '~/commons/components/ListHttpTable.jsx';
import FormHttpBtn from '~/commons/components/FormHttpBtn.jsx';
import FormHttpFileBtn from '~/commons/components/FormHttpFileBtn.jsx';
import ModalConfirm from '~/commons/components/ModalConfirm.jsx';
import ModalAlert from '~/commons/components/ModalAlert.jsx';
import links from '../layout/NavList';

const PageResource = ({
  api,
  sort,
  cols,
  searchable = false,
  filter = {},
  actions: {
    fileImport = false,
    fileExport = false,
    find = true,
    create = true,
    remove = true,
  } = {},
  extraActions = [],
  basePath = '',
  onRowClick,
  ctrls = [],
  children = () => { },
  segment = '',
}) => {
  const history = useHistory();
  const [selected, setSelected] = useState([]);
  const [fSearch, setFSearch] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [res, req] = useHttpStatus();
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [removeStatus, setRemoveStatus] = useState('');
  const [uploadSuccess, setUploadSucess] = useState(false);
  const [curPage, setCurPage] = useState(1);
  const { path } = useRouteMatch();
  const [searchParams, setSearchParams] = useSearchParams({ searchBy: _.first(searchable)?.value });
  const canEdit = permitCheck(links, path, segment);
  const debouncedSearch = useCallback(
    debounce(
      (v) => {
        setFSearch(v);
      },
      1500,
      { leading: true },
    ), [],
  );

  let searchOpts;

  if (Array.isArray(searchable)) {
    const searchBy = searchParams.get('searchBy');
    const c = searchable.find((s) => s.value === searchBy);
    searchOpts = c?.options;
  }

  const bPath = typeof basePath === 'function' ? basePath : () => basePath;

  const f = { ...filter };

  if (searchable && fSearch) {
    f.search = fSearch;
    if (searchParams.get('searchBy')) {
      f.searchBy = searchParams.get('searchBy');
    }
  }

  useEffect(() => {
    // To search the results again
    // after users clicked back button of the browser from the detail view.
    let curPageValue = searchParams.get('curPage');
    if (curPageValue == null) {
      curPageValue = 1;
    }
    setCurPage(curPageValue);

    debouncedSearch(searchParams.get('search'));
  }, [useComparable(searchable)]);

  return (
    <div>
      {(remove && canEdit) && (
        <ModalConfirm
          show={removeConfirm}
          onReject={() => setRemoveConfirm(false)}
          onConfirm={async () => {
            setRemoveConfirm(false);

            const { code, payload } = await req({
              url: api,
              method: 'delete',
              data: { _id: selected.map((s) => s._id) },
            });
            setRefresh(refresh + 1);
            setRemoveStatus(code === 200 ? 'Success' : String(payload));
          }}
        >
          Are you sure you want to delete?
        </ModalConfirm>
      )}
      {(removeStatus) && (
        <ModalAlert
          size="lg"
          show={!!removeStatus}
          onHide={() => setRemoveStatus('')}
          icon={removeStatus === 'Success' ? faCheckCircle : faTimesCircle}
          theme={removeStatus === 'Success' ? 'success' : 'danger'}
        >
          {removeStatus}
        </ModalAlert>
      )}
      {uploadSuccess && (
        <ModalAlert
          show={!!uploadSuccess}
          icon={uploadSuccess?.showIcon ?? true ? faCheckCircle : null}
          theme="success"
          onHide={() => setUploadSucess(false)}
        >
          {(uploadSuccess?.showIcon ?? true) && 'Upload success'}
          {uploadSuccess.children}
        </ModalAlert>
      )}
      <div className="pb-3 clearfix">
        {(create && canEdit) && (
          <Link
            to={resolveTo(bPath('new'), 'new')}
            className="btn btn-primary mr-2"
          >
            <FA icon={faPlus} fixedWidth />
          </Link>
        )}
        {(fileImport && canEdit) && (
          <FormHttpFileBtn
            className="btn btn-secondary mr-2"
            icon={faUpload}
            onSuccess={() => {
              setRefresh(refresh + 1);
              setUploadSucess(true);
            }}
            api={{
              url: `${api}/${typeof fileImport === 'string' ? fileImport : 'import'
              }`,
              ...fileImport,
            }}
            fileOpts={{
              accept: fileImport?.accept || '.csv',
            }}
          />
        )}
        {fileExport && (
          <a
            className="btn btn-secondary mr-2"
            href={`${api}/${typeof fileExport === 'string' ? fileExport : 'export'
            }${(() => {
              const curFilter = Object.values(f).filter((s) => s).length ? f : null;
              const sort2 = Object.keys(sort) ? sort : null;
              return curFilter || sort2 ? `?${qs.stringify(pickBy({ filter: curFilter, sort: sort2 }, (s) => s))}` : '';
            })()}`}
            download
          >
            <FA icon={faDownload} fixedWidth />
          </a>
        )}
        {(remove && canEdit) && selected.length > 0 && (
          <FormHttpBtn
            res={res}
            className="btn btn-link"
            icon={faTrash}
            onClick={() => setRemoveConfirm(true)}
          />
        )}
        {map(extraActions, (action, i) => {
          switch (action.type) {
            case 'import':
              return canEdit && (
                <FormHttpFileBtn
                  key={i}
                  className="btn btn-secondary mr-2"
                  icon={faUpload}
                  onSuccess={(r) => {
                    setRefresh(refresh + 1);
                    setUploadSucess(action.onSuccess?.(r) || true);
                  }}
                  {...omit(action, 'type', 'onSuccess')}
                />
              );
            default:
          }
          return null; // To prevent the error message from VS Code.
        })}
        {children({
          selected,
          refresh: () => setRefresh(refresh + 1),
        })}
      </div>
      <ListHttpTable
        {...{
          api: { url: api },
          sort,
          skip: (((curPage == null ? 1 : curPage) - 1) * 20), // 20 = limit
          cols,
          filter: f,
          refresh,
          segment,
          onSelect: (remove && canEdit)
            ? (s) => { setSelected(s); }
            : undefined,
          ...(find ? {
            onRowClick: onRowClick || ((row) => {
              history.push(resolveTo(bPath(row), row._id));
            }),
          } : {}),
        }}
      >
        <div className="form-row">
          {searchable && (
            <div className="col">
              <div className="input-group">
                {Array.isArray(searchable) && (
                  <div className="input-group-prepend">
                    <select
                      name="searchBy"
                      className="custom-select"
                      value={searchParams.get('searchBy') ? searchParams.get('searchBy') : ''}
                      onChange={(e) => {
                        debouncedSearch('');
                        setSearchParams({ searchBy: e.target.value });
                      }}
                    >
                      {searchable.map((by) => (
                        <option
                          key={by.value || by}
                          value={by.value || by}
                        >
                          {by.label || startCase(by)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {searchOpts ? (
                  <select
                    name="search"
                    className="custom-select"
                    value={searchParams.get('search') ? searchParams.get('search') : ''}
                    onChange={(e) => {
                      const { value } = e.target;
                      debouncedSearch(value);
                      setSearchParams({ search: value });
                    }}
                  >
                    <option value="">Show All</option>
                    {searchOpts.map((o) => (
                      <option
                        key={o.value}
                        value={o.value}
                      >{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    name="search"
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    value={searchParams.get('search') ? searchParams.get('search') : ''}
                    onChange={(e) => {
                      const { value } = e.target;
                      debouncedSearch(value);
                      setSearchParams({ search: value });
                    }}
                  />
                )}
              </div>
            </div>
          )}
          {ctrls.map((c, i) => (
            <div className="col-auto" key={i}>
              {c}
            </div>
          ))}
        </div>
      </ListHttpTable>
    </div>
  );
};

export default PageResource;
